import {
  ChangeOrganizationRequest,
  CreateOrganizationRequest,
  Organization,
  OrganizationCategory,
  OrganizationType,
  UserMembershipInfo,
  UserProfile
} from '@slr/user-service-external-sdk';
import { formatOrganizationAddress } from 'utils/formatOrganizationAddress';

type BillingAddressLike = {
  addressType?: string | null;
  companyName?: string | null;
  taxNumber?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
};

type Solution1ProfileLike = {
  billingAddress?: BillingAddressLike | null;
};

export type EnsureBusinessOrganizationOptions = {
  syncRegistrationAddress?: boolean;
};

type EnsureBusinessOrganizationDeps = {
  findMembershipsOfUser: () => Promise<UserMembershipInfo[] | void>;
  createOrganization: (
    params: CreateOrganizationRequest
  ) => Promise<Organization | void>;
  getOrganization: (params: {
    organizationId: string;
  }) => Promise<Organization | void>;
  changeOrganization: (
    params: ChangeOrganizationRequest
  ) => Promise<Organization | void>;
  getSolution1UserProfile: () => Promise<Solution1ProfileLike | void>;
  getUserProfile: () => Promise<UserProfile | void>;
  options?: EnsureBusinessOrganizationOptions;
};

export const ensureBusinessOrganization = async ({
  findMembershipsOfUser,
  createOrganization,
  getOrganization,
  changeOrganization,
  getSolution1UserProfile,
  getUserProfile,
  options
}: EnsureBusinessOrganizationDeps): Promise<boolean> => {
  const solution1Profile = await getSolution1UserProfile();
  const billingAddress = solution1Profile?.billingAddress;
  if (billingAddress?.addressType !== 'business') {
    return false;
  }

  const companyName = billingAddress.companyName?.trim();
  if (!companyName) {
    return false;
  }

  const formattedAddress = formatOrganizationAddress(billingAddress);
  const memberships = await findMembershipsOfUser();

  if (memberships?.length) {
    if (!options?.syncRegistrationAddress || !formattedAddress) {
      return false;
    }

    const membership = memberships[0];
    const organization = await getOrganization({
      organizationId: membership.organizationId
    });
    if (!organization || organization.address?.trim()) {
      return false;
    }

    await changeOrganization({
      organizationId: membership.organizationId,
      changeOrganizationRequestDto: {
        name: companyName,
        email: organization.email ?? undefined,
        phoneNumber: organization.phoneNumber ?? undefined,
        address: formattedAddress,
        description: organization.description ?? undefined,
        detailedDescription: organization.detailedDescription ?? undefined,
        websiteUrl: organization.websiteUrl ?? undefined,
        taxNumber:
          organization.taxNumber?.trim() ||
          billingAddress.taxNumber?.trim() ||
          undefined,
        mediaItemId: organization.logoId ?? undefined
      }
    });
    return true;
  }

  const userProfile = await getUserProfile();
  const createdOrganization = await createOrganization({
    organizationType: OrganizationType.User,
    createOrganizationRequestDto: {
      name: companyName,
      email: userProfile?.emailAddress ?? undefined,
      taxNumber: billingAddress.taxNumber?.trim() || undefined,
      address: options?.syncRegistrationAddress ? formattedAddress : undefined,
      category: OrganizationCategory.BusinessOrg
    }
  });

  return createdOrganization != null;
};
